import { localizedLongDate } from './helpers'
import { luxonTimeFormat } from './time_format'

export class MobileBookingFlowManager {
  constructor({
    locale,
    datePickerController,
    durationInMinutesValue,
    showBookingForm,
    hideBookingForm,
    getSlots,
    onTimeZoneChange,
    toLuxonDateTime,
    toLuxonDateTimeKeepingSameTime,
    slotPickerElements,
  }) {
    this.locale = locale
    this.datePickerController = datePickerController
    this.durationInMinutesValue = durationInMinutesValue
    this.showBookingForm = showBookingForm
    this.hideBookingForm = hideBookingForm
    this.getSlots = getSlots
    this.toLuxonDateTime = toLuxonDateTime
    this.toLuxonDateTimeKeepingSameTime = toLuxonDateTimeKeepingSameTime
    this.slotPickerElements = slotPickerElements

    // This is a template for an option of the time slot picker
    this.slotPickerOptionTemplate =
      this.slotPickerElements.buttonGroup.children[0]
    // The button group renders this option preselected; drop its selected style too, or every
    // rebuilt slot looks picked (the button group only clears it once, when first shown).
    const activeClasses = (
      this.slotPickerOptionTemplate.closest('[data-button-group-active-class]')
        ?.dataset.buttonGroupActiveClass || ''
    )
      .split(/\s+/)
      .filter(Boolean)
    this.slotPickerOptionTemplate.classList.remove('hidden', ...activeClasses)
    this.slotPickerOptionTemplate.remove()
    this.slotPickerOptionTemplate =
      this.slotPickerOptionTemplate.outerHTML.replaceAll(
        'checked="checked"',
        ''
      )

    // When someone changes the timezone, let's update the slots shown on the slot picker
    onTimeZoneChange((tz) => {
      this.timeZone = tz
      this.updateSlotPicker()
    })

    // When someone selects a date on the date picker, let's show the time slots for that date
    this.attachDateChangeListener()

    // Attach a click listener for when someone clicks on a slot picker item
    this.attachSlotPickerItemSelectListener()

    // Rebuild slot labels when the visitor toggles 24h.
    document.addEventListener('time-format:change', this.onTimeFormatChange)
  }

  onTimeFormatChange = () => {
    if (this.selectedDate) this.updateSlotPicker()
  }

  // Remove the document listener so a stale manager can't rebuild the slot
  // picker against detached DOM after a Turbo/Stimulus reconnect.
  destroy() {
    document.removeEventListener('time-format:change', this.onTimeFormatChange)
  }

  hideSlotPicker = () => {
    this.slotPickerElements.container.classList.add('hidden')
    this.datePickerController.unselect()
  }

  /***************
    Private stuff
  ***************/

  attachDateChangeListener() {
    this.datePickerController.onDateSelect((date) => {
      if (!date) return
      this.selectedDate = date
      this.showSlotPicker()
    })
  }

  attachSlotPickerItemSelectListener() {
    this.slotPickerElements.buttonGroup.addEventListener('change', (e) => {
      const start = this.toLuxonDateTime(new Date(e.target.value))
      const end = start.plus({ minutes: this.durationInMinutesValue })
      this.showBookingForm({ slot: { start, end }, type: 'normal' })
    })
  }

  showSlotPicker() {
    this.hideBookingForm()
    this.updateSlotPicker()
    this.slotPickerElements.container.classList.remove('hidden')
  }

  // The person booking picked another length; slots are rebuilt for it.
  setDuration(minutes) {
    this.durationInMinutesValue = minutes
    this.updateSlotPicker()
  }

  async updateSlotPicker() {
    const selectedDate = this.selectedDate
    if (!selectedDate) return
    let dateLuxon = this.toLuxonDateTimeKeepingSameTime(selectedDate)
    if (this.timeZone) {
      dateLuxon = dateLuxon.setZone(this.timeZone)
    }

    this.slotPickerElements.date.innerText = localizedLongDate(
      dateLuxon,
      this.locale
    )

    const startOfDay = dateLuxon.startOf('day')
    const endOfDay = dateLuxon.endOf('day')

    // A newer refresh (another date, length or time zone) may start while this one waits for the feed.
    const refresh = (this.slotPickerRefresh = (this.slotPickerRefresh || 0) + 1)
    this.slotPickerElements.buttonGroup.innerHTML = ''
    const slots = await this.getSlots(startOfDay, endOfDay)
    if (refresh !== this.slotPickerRefresh) return

    slots.forEach((slot) => {
      const option = this.slotPickerOptionTemplate
        .replaceAll('___LABEL___', slot.start.toFormat(luxonTimeFormat()))
        .replaceAll('___VALUE___', slot.start.toISO())
      this.slotPickerElements.buttonGroup.innerHTML += option
    })

    this.slotPickerElements.emptyState.classList.toggle(
      'hidden',
      !!slots.length
    )
  }
}
